import { useEffect, useState } from 'react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface PdfPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** A directly-loadable URL (e.g. a public media path used in an <a href>). */
  src?: string | null;
  /**
   * For auth-protected sources: fetch the bytes yourself (so the JWT/org headers
   * are attached, just like the blob download). Takes precedence over `src`.
   */
  fetchBlob?: () => Promise<Blob>;
}

/**
 * Inline PDF preview, mirroring the document-attachment "eye" preview: an iframe
 * inside a dialog. Direct URLs render straight away; auth-protected files are
 * fetched as a blob and shown via an object URL (revoked on close).
 */
export function PdfPreviewDialog({ open, onOpenChange, title, src, fetchBlob }: PdfPreviewDialogProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // Callers pass a `useCallback`-stable `fetchBlob`, so this only re-runs when
  // the dialog opens or the target invoice changes.
  useEffect(() => {
    if (!open || !fetchBlob) return;

    let cancelled = false;
    let url: string | null = null;
    fetchBlob()
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    // Reset on close / target change (also clears a prior failure on reopen).
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
      setFailed(false);
    };
  }, [open, fetchBlob]);

  const resolvedSrc = fetchBlob ? blobUrl : src;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate">{title}</DialogTitle>
        </DialogHeader>
        {failed ? (
          <p className="text-destructive py-8 text-center text-sm">Could not load the document.</p>
        ) : resolvedSrc ? (
          <iframe src={resolvedSrc} title={title} className="h-[70vh] w-full rounded-md border" />
        ) : (
          <Skeleton className="h-[70vh] w-full" />
        )}
      </DialogContent>
    </Dialog>
  );
}
